/**
 * Export Button Component
 * Handles PDF, DOCX, and Excel export for report templates
 */

import React, { useState } from 'react';
import { Download, FileText, FileSpreadsheet, Table, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '../ui/dropdown-menu';
import { useToast } from '../../hooks/use-toast';
import { useAuth } from '../../contexts/AuthContext';
import { useBusinessPlan } from '../../App';
import { PDFGenerator, DOCXGenerator, ExcelGenerator } from './services/ReportGenerator';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ExportButton = ({ templateId, templateName = 'report', variant = 'default', size = 'default' }) => {
  const [loading, setLoading] = useState(false);
  const [exportType, setExportType] = useState(null);
  const { token } = useAuth();
  const { currentPlan } = useBusinessPlan();
  const { toast } = useToast();

  const handleExport = async (format) => {
    if (!templateId || !currentPlan?.id) {
      toast({
        title: 'Export Error',
        description: 'Template or business plan not selected',
        variant: 'destructive',
      });
      return;
    }

    setLoading(true);
    setExportType(format);

    try {
      // Fetch report data from backend
      const params = new URLSearchParams({
        plan_id: currentPlan.id,
        format,
      });

      const response = await fetch(
        `${BACKEND_URL}/api/report-templates/generate/${templateId}?${params}`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to generate report');
      }

      const reportData = await response.json();

      // Generate document based on format
      if (format === 'pdf') {
        const generator = new PDFGenerator(reportData);
        await generator.generate();
        generator.download(`${templateName.replace(/\s+/g, '_')}.pdf`);
      } else if (format === 'docx') {
        const generator = new DOCXGenerator(reportData);
        await generator.download(`${templateName.replace(/\s+/g, '_')}.docx`);
      } else if (format === 'xlsx') {
        const generator = new ExcelGenerator(reportData);
        await generator.download(`${templateName.replace(/\s+/g, '_')}.xlsx`);
      }

      toast({
        title: 'Export Successful',
        description: `${format.toUpperCase()} report downloaded successfully`,
      });
    } catch (error) {
      console.error('Export error:', error);
      toast({
        title: 'Export Failed',
        description: error.message || 'Failed to generate report',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
      setExportType(null);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant={variant} size={size} disabled={loading}>
          {loading ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Generating {exportType?.toUpperCase()}...
            </>
          ) : (
            <>
              <Download className="h-4 w-4 mr-2" />
              Export
            </>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem
          onClick={() => handleExport('pdf')}
          disabled={loading}
          className="cursor-pointer"
        >
          <FileText className="h-4 w-4 mr-2 text-red-500" />
          Export as PDF
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => handleExport('docx')}
          disabled={loading}
          className="cursor-pointer"
        >
          <FileSpreadsheet className="h-4 w-4 mr-2 text-blue-500" />
          Export as Word
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={() => handleExport('xlsx')}
          disabled={loading}
          className="cursor-pointer"
        >
          <Table className="h-4 w-4 mr-2 text-green-600" />
          Export as Excel
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default ExportButton;
